import { isMobileAnim } from "./mobile-anim-gate-Btn1wVuD.js";
let animBatch = [];
let animBatchScheduled = false;
const pendingReverts = [];
let revertIdle = null;
function scheduleRevert(fn) {
  pendingReverts.push(fn);
  if (!revertIdle) {
    const execute = () => {
      revertIdle = null;
      pendingReverts.splice(0).forEach((r) => r());
    };
    if (window.requestIdleCallback) {
      revertIdle = window.requestIdleCallback(execute);
    } else {
      revertIdle = setTimeout(execute, 1);
    }
  }
}
function processAnimBatch() {
  const currentBatch = animBatch;
  animBatch = [];
  animBatchScheduled = false;
  const clipData = /* @__PURE__ */ new Map();
  if (!isMobileAnim) {
    currentBatch.forEach((item) => {
      if (item.el.dataset.animUnclipParent === "true") {
        const parent = item.el.parentElement;
        if (parent) {
          const style = window.getComputedStyle(parent);
          clipData.set(item.el, {
            parent,
            // Cache the original inline styles so we can restore them exactly
            overflow: parent.style.overflow,
            contain: parent.style.contain,
            computedOverflow: style.overflow,
            computedContain: style.contain
          });
        }
      }
    });
  }
  currentBatch.sort((a, b) => {
    if (a.el === b.el) return 0;
    return a.el.compareDocumentPosition(b.el) & 2 ? 1 : -1;
  });
  let effectiveIndex = 0;
  let lastSyncVal = null;
  let accumulatedStagger = 0;
  currentBatch.forEach((item, rawIndex) => {
    if (!item.el.isConnected) return;
    if (!item.isStandalone && !item.el._ready) return;
    let computedDelay;
    if (item.preserveBaseDelay) {
      computedDelay = item.baseDelay;
    } else {
      const currentSyncVal = item.el.dataset.animSync;
      let delta = 0;
      if (currentSyncVal && currentSyncVal === lastSyncVal) ;
      else {
        effectiveIndex++;
        lastSyncVal = currentSyncVal;
        if (effectiveIndex > 1) {
          if (isMobileAnim) {
            delta = 40 / Math.max(item.staggerAmount, 1);
          } else {
            const decay = parseFloat(item.el.dataset.animStaggerDecay) || 0.85;
            const minDelta = parseFloat(item.el.dataset.animStaggerMin) || 0.15;
            const decayedCurrent = Math.pow(effectiveIndex - 1, decay);
            const decayedPrev = Math.pow(effectiveIndex - 2, decay);
            delta = Math.max(decayedCurrent - decayedPrev, minDelta);
          }
        }
      }
      accumulatedStagger += delta * item.staggerAmount;
      computedDelay = item.baseDelay + accumulatedStagger;
    }
    const clip = clipData.get(item.el);
    if (clip) {
      clip.parent.style.setProperty("overflow", "visible", "important");
      clip.parent.style.setProperty("contain", "none", "important");
    }
    const restoreClip = () => {
      if (clip) {
        if (clip.overflow) {
          clip.parent.style.overflow = clip.overflow;
        } else {
          clip.parent.style.removeProperty("overflow");
        }
        if (clip.contain) {
          clip.parent.style.contain = clip.contain;
        } else {
          clip.parent.style.removeProperty("contain");
        }
      }
    };
    item.animate(computedDelay, restoreClip);
  });
  animBatch = [];
  animBatchScheduled = false;
}
function queueAnim(entry) {
  animBatch.push(entry);
  if (!animBatchScheduled) {
    animBatchScheduled = true;
    requestAnimationFrame(processAnimBatch);
  }
}
const flushingArrays = /* @__PURE__ */ new WeakSet();
function flushVisiblePending(pendingArray, mainObserver, extraUnobserve = null) {
  if (pendingArray.length === 0 || flushingArrays.has(pendingArray)) return;
  flushingArrays.add(pendingArray);
  const checkObserver = new IntersectionObserver((entries) => {
    checkObserver.disconnect();
    const toTrigger = [];
    const toUnobserve = [];
    for (const entry of entries) {
      if (entry.isIntersecting) {
        const el = entry.target;
        const idx = pendingArray.indexOf(el);
        if (idx > -1) {
          if (el.dataset.animRepeat !== "true") {
            toUnobserve.push(el);
            pendingArray.splice(idx, 1);
            el._isObserved = false;
          }
          toTrigger.push(el);
        }
      }
    }
    requestAnimationFrame(() => {
      for (const el of toUnobserve) {
        mainObserver.unobserve(el);
        if (extraUnobserve) extraUnobserve(el);
      }
      for (const el of toTrigger) {
        if (el._triggerReveal) el._triggerReveal();
      }
    });
    flushingArrays.delete(pendingArray);
  }, { rootMargin: "0px", threshold: 0 });
  let observedCount = 0;
  for (let i = pendingArray.length - 1; i >= 0; i--) {
    const el = pendingArray[i];
    if (!el || !el.isConnected) {
      pendingArray.splice(i, 1);
      continue;
    }
    checkObserver.observe(el);
    observedCount++;
  }
  if (observedCount === 0) {
    checkObserver.disconnect();
    flushingArrays.delete(pendingArray);
  }
}
let globalSentryObs = null;
const sentryCallbacks = /* @__PURE__ */ new Set();
function registerSentryFlush(callback) {
  sentryCallbacks.add(callback);
  if (!globalSentryObs) {
    const sentry = document.createElement("div");
    sentry.style.cssText = "position:absolute; bottom:0; width:1px; height:1px; pointer-events:none; opacity:0;";
    document.body.appendChild(sentry);
    globalSentryObs = new IntersectionObserver((entries) => {
      if (entries[0].isIntersecting) {
        sentryCallbacks.forEach((cb) => cb());
      }
    }, { rootMargin: "0px", threshold: 0 });
    globalSentryObs.observe(sentry);
  }
}
function unregisterSentryFlush(callback) {
  sentryCallbacks.delete(callback);
}
export {
  flushVisiblePending,
  queueAnim,
  registerSentryFlush,
  scheduleRevert,
  unregisterSentryFlush
};
